import { CountryCode } from "@silversea-ssc/dotcom-read-model-types/Common";
import { FareCode } from "@silversea-ssc/web-fares";
import FYCQueryBuilder, {
  FYCQuery
} from "components/FindYourCruise/FYCQueryBuilder";
import { arrayIsNotEmpty, isPresent } from "src/utils/checks";
import { splitCodesStringList, stringPresent } from "src/utils/strings";
import {
  Maybe,
  SanityCruiseData,
  SanityCruisesLaunch,
  SanityDestination,
  SanityExclusiveOfferPerPromo,
  SanityLandProgramme,
  SanityMultilingualArrayString,
  SanityMultilingualString,
  SanityPortData,
  SanityPromoConfiguration,
  SanityShip,
  SanitySpecialVoyageData
} from "types/graphql-types";
import { Language } from "src/utils/localization/languages/types";
import { generateRange, getMonth } from "components/MonthCalendar/utils";

type Args = {
  filterByCruiseType?: (string | undefined)[];
  filterByComboType?: (string | undefined)[];
  filterByFeatures?: (string | undefined)[];
  filterByCruise?: Maybe<
    | { data?: Maybe<Pick<SanityCruiseData, "cruiseCode">> }
    | { data?: Maybe<Pick<SanitySpecialVoyageData, "cruiseCode">> }
  >[];
  filterByCruiseCodes?: string;
  filterByDestination?: Maybe<{
    name?: Maybe<Pick<SanityMultilingualString, "localized">>;
  }>[];
  filterByOffer?: Maybe<
    Maybe<
      Pick<SanityExclusiveOfferPerPromo, "_id"> & {
        configuration?: Maybe<Pick<SanityPromoConfiguration, "algoliaCode">>;
      }
    >[]
  >;
  filterByShip?: Maybe<Maybe<Pick<SanityShip, "name">>[]>;
  filterByPorts?: Maybe<
    Maybe<{ data?: Maybe<Pick<SanityPortData, "code">> }>[]
  >;
  filterByCountries?: Maybe<
    Maybe<{
      ports?: Maybe<Maybe<{ data?: Maybe<Pick<SanityPortData, "code">> }>[]>;
    }>[]
  >;
  filterByCruiseGroups?: (string | undefined)[];
  filterByLandProgrammes?: Maybe<Maybe<Pick<SanityLandProgramme, "code">>[]>;
  filterByFareCodes?: Maybe<Maybe<string>[]>;
  filterByCruisesLaunch?: Maybe<Pick<SanityCruisesLaunch, "cruiseCodes">>;
  filterByLoadStress?: Maybe<Maybe<string>[]>;
  filterByDepartureYears?: Maybe<Maybe<string>[]>;
  filterByDepartureYearsAndMonths?: Maybe<
    | Maybe<{
        year?: string | undefined;
        months?: (string | undefined)[] | undefined;
      }>[]
    | undefined
  >;
  filterByCruiseTagCategories?: Maybe<
    Maybe<{
      title?: Maybe<Pick<SanityMultilingualArrayString, "localized">>;
    }>[]
  >;
};

export const configToFYCQuery = (
  {
    filterByComboType,
    filterByCountries,
    filterByCruise,
    filterByCruiseCodes,
    filterByCruiseGroups,
    filterByCruiseType,
    filterByCruisesLaunch,
    filterByDestination,
    filterByFareCodes,
    filterByFeatures,
    filterByOffer,
    filterByPorts,
    filterByShip,
    filterByLoadStress,
    filterByDepartureYears,
    filterByDepartureYearsAndMonths,
    filterByCruiseTagCategories,
    filterByLandProgrammes
  }: Args,
  countryCode: CountryCode | undefined,
  language: Language,
  showOnlyAvailableCruises: boolean = true
): FYCQuery => {
  const fycQueryBuilder = new FYCQueryBuilder();
  const typedFilterByFareCodes = (filterByFareCodes?.filter(isPresent) ||
    []) as FareCode[];

  const allFiltersByCruiseCodes = [
    ...(filterByCruise || []).map((c) => c?.data?.cruiseCode),
    ...parseCruiseCodesToCruiseCodesArray(filterByCruiseCodes),
    ...parseCruiseCodesToCruiseCodesArray(filterByCruisesLaunch?.cruiseCodes)
  ].filter(stringPresent);

  if (allFiltersByCruiseCodes.length > 0) {
    fycQueryBuilder.filterByCruiseCodes(allFiltersByCruiseCodes);
  }

  if (filterByCruiseType?.length)
    fycQueryBuilder.filterByCruiseTypes(filterByCruiseType.filter(isPresent));

  if (filterByComboType?.length)
    fycQueryBuilder.filterByComboTypes(filterByComboType.filter(isPresent));

  if (filterByFeatures?.length)
    fycQueryBuilder.filterByFeatures(filterByFeatures.filter(isPresent));

  if (filterByCruiseGroups?.length)
    fycQueryBuilder.filterByCruiseGroups(
      filterByCruiseGroups.filter(isPresent)
    );

  if (typedFilterByFareCodes.length > 0)
    fycQueryBuilder.filterByFareCodes(typedFilterByFareCodes);

  if (filterByOffer?.length) {
    const promoCodes = filterByOffer
      .map((o) => o?.configuration?.algoliaCode)
      .filter(isPresent);
    if (promoCodes.length)
      fycQueryBuilder.filterByPromoCodes(promoCodes, countryCode);
  }

  if (filterByDestination?.length)
    fycQueryBuilder.filterByDestinationNames(
      filterByDestination.map((d) => d?.name?.localized).filter(isPresent),
      language
    );

  if (filterByShip?.length)
    fycQueryBuilder.filterByShipsName(
      filterByShip.map((s) => s?.name).filter(isPresent)
    );

  if (filterByPorts?.length) {
    const portCodes = filterByPorts.map((p) => p?.data?.code).filter(isPresent);
    fycQueryBuilder.filterByPorts(portCodes);
  }

  if (filterByCountries?.length) {
    const portCodes = filterByCountries
      .flatMap((country) => country?.ports?.map((p) => p?.data?.code))
      .filter(isPresent);
    fycQueryBuilder.filterByPorts(portCodes);
  }

  if (filterByLoadStress?.length) {
    fycQueryBuilder.filterByLoadStress(filterByLoadStress.filter(isPresent));
  }

  if (filterByCruiseTagCategories?.length) {
    const tagCategoryTitles = filterByCruiseTagCategories
      ?.map((cat) => cat?.title?.localized)
      .filter(isPresent);
    if (tagCategoryTitles?.length) {
      fycQueryBuilder.filterByCruiseTagCategories(tagCategoryTitles, language);
    }
  }

  if (filterByLandProgrammes) {
    const landProgrammeCodes = filterByLandProgrammes
      .map((lp) => lp?.code)
      .filter(isPresent);
    if (landProgrammeCodes.length > 0) {
      fycQueryBuilder.filterByLandProgrammes(landProgrammeCodes);
    }
  }

  const uniqueFilterByDepartureYears = (filterByDepartureYears || []).filter(
    isPresent
  );
  if (uniqueFilterByDepartureYears.length) {
    fycQueryBuilder.filterByDepartureYears(uniqueFilterByDepartureYears);
  }

  const uniqueFilterByDepartureYearsAndMonths = (
    filterByDepartureYearsAndMonths || []
  ).filter(isPresent);

  if (uniqueFilterByDepartureYearsAndMonths.length > 0) {
    fycQueryBuilder.filterByDepartureYearsAndMonths(
      uniqueFilterByDepartureYearsAndMonths
    );
  }

  if (showOnlyAvailableCruises) {
    fycQueryBuilder.onlyAvailableCruises();
  }
  return fycQueryBuilder.generate();
};

const parseCruiseCodesToCruiseCodesArray = (
  cruiseCodes: string | undefined
): string[] => {
  if (!cruiseCodes) return [];
  return splitCodesStringList(cruiseCodes);
};

export const buildDepartureYearsMonths = (
  filterByDepartureYears: (string | undefined)[] | undefined,
  filterByDepartureYearsAndMonths:
    | (
        | {
            year?: string;
            months?: (string | undefined)[];
          }
        | undefined
      )[]
    | undefined
) => {
  const yearsMonths = filterByDepartureYearsAndMonths
    ?.filter(
      (x): x is { year: string; months: string[] } =>
        isPresent(x) && arrayIsNotEmpty(x.months) && isPresent(x.year)
    )
    .flatMap(({ year, months }) =>
      months.map((monthIndex) =>
        getMonth(parseInt(year, 10), parseInt(monthIndex, 10) - 1)
      )
    );

  const staticYears =
    filterByDepartureYears?.filter(isPresent).flatMap(buildDepartureYear) || [];

  if (arrayIsNotEmpty(yearsMonths)) {
    return yearsMonths;
  }

  if (arrayIsNotEmpty(staticYears)) {
    return staticYears;
  }

  return [];
};

const buildDepartureYear = (year: string) => {
  return generateRange(12).map((monthIndex) => {
    return getMonth(parseInt(year, 10), monthIndex);
  });
};
